/* eslint-disable @typescript-eslint/no-unsafe-return */
import { FunctionalComponent, VNode, h } from '@stencil/core';
import { generateSVGImage } from 'utils/imageHelper';
import { height as iconPauseHeight, svgPathData as iconPausePath, width as iconPauseWidth } from '@fortawesome/pro-light-svg-icons/faPauseCircle';
import { height as iconPlayHeight, svgPathData as iconPlayPath, width as iconPlayWidth } from '@fortawesome/pro-light-svg-icons/faPlayCircle';

/**
 * Interface for the bg-video component
 */
interface IBgVideoProps {
  videoUrlLarge?: string;
  videoUrlMedium?: string;
  videoUrlSmall?: string;
  pauseText?: string;
  playText?: string;
  enableVideoControl?: boolean;
  useInvertedColorVideoControl?: boolean;
  customColorVideoControl?: string;
  poster?: string; // New optional poster property
  isPlaying: boolean;
  onPlay: () => void;
  onPause: () => void;
  parallax?: boolean;
  enableVideoControlText?: boolean;
}

export const BgVideo: FunctionalComponent<IBgVideoProps> = ({
  videoUrlLarge,
  videoUrlMedium,
  videoUrlSmall,
  enableVideoControl = false,
  useInvertedColorVideoControl = false,
  customColorVideoControl = 'black',
  pauseText = 'Pause',
  playText = 'Play',
  poster,
  isPlaying = true,
  onPlay,
  onPause,
  parallax = false,
  enableVideoControlText = false,
}: IBgVideoProps): VNode => {
  let videoElement: HTMLVideoElement | null = null;

  const parseVideoUrl = (videoUrl: string | undefined): string | undefined => {
    if (!videoUrl) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(videoUrl) as { videoUrl: string };
      return parsed.videoUrl;
    } catch {
      return undefined;
    }
  };

  const getVideoUrl = (): string => {
    const width = window.innerWidth;

    if (videoUrlSmall && width <= 600) {
      return parseVideoUrl(videoUrlSmall) ?? parseVideoUrl(videoUrlLarge) ?? '';
    } else if (videoUrlMedium && width <= 1024) {
      return parseVideoUrl(videoUrlMedium) ?? parseVideoUrl(videoUrlLarge) ?? '';
    } else if (videoUrlLarge) {
      return parseVideoUrl(videoUrlLarge) ?? '';
    }
    return ''; // Fallback to an empty string if no video URL is available
  };

  const cropCenterPart = (): void => {
    if (videoElement) {
      videoElement.style.objectFit = 'cover';
      videoElement.style.width = '100%';
      videoElement.style.height = '100%';
    }
  };

  const playVideo = () => {
    videoElement?.play().catch((error) => console.error('Error playing video:', error));
    onPlay();
  };

  const pauseVideo = () => {
    videoElement?.pause();
    onPause();
  };

  const videoUrl = getVideoUrl();

  return (
    <div class="video-container">
      {videoUrl ? (
        <video
          id="bg-video"
          class={parallax ? 'parallax-video' : 'bg-video'}
          autoplay
          muted
          loop
          ref={(el) => {
            videoElement = el;
            videoElement?.addEventListener('loadedmetadata', cropCenterPart);
          }}
          src={videoUrl}
          poster={poster} // Using the new poster property
          style={{ color: customColorVideoControl }}
        >
          Your browser does not support the video tag.
        </video>
      ) : (
        <p>No video available</p>
      )}
      {enableVideoControl && (
        <div class={`video-controls ${useInvertedColorVideoControl ? 'inverted' : ''}`}>
          <slot name="video-header"></slot>
          <slot name="video-description"></slot>
          <div class="button-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            {isPlaying ? (
              <span id="ctrlBtn" onClick={pauseVideo} style={{ cursor: 'pointer' }}>
                {generateSVGImage({ data: iconPausePath, height: iconPauseHeight, width: iconPauseWidth })}
                {enableVideoControlText && <span style={{ color: customColorVideoControl }}>{pauseText}</span>}
              </span>
            ) : (
              <span id="ctrlBtn" onClick={playVideo} style={{ cursor: 'pointer' }}>
                {generateSVGImage({ data: iconPlayPath, height: iconPlayHeight, width: iconPlayWidth })}
                {enableVideoControlText && <span style={{ color: customColorVideoControl }}>{playText}</span>}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
